import React, { useEffect, useMemo, useState } from "react";
import {
  countLabel,
  jobKey,
  jobMatches,
  jobName,
  jobStaff,
  pathToNode,
  rollupRoots,
} from "./hierarchyModel";

const ROOT_ID = "__root__";
const JOB_LIMIT = 12;
// Large job lists load a page at a time and can be filtered.
const JOB_PAGE = 48;
const JOB_FILTER_MIN = 24;

// The drill-down chart — never renders the whole org at once. Shows one focused
// node with its immediate child functions as cards and its jobs below;
// clicking a card drills in, and a breadcrumb climbs back up.
const HierarchyChartView = ({ hierarchy, selectedCompany }) => {
  const [focusId, setFocusId] = useState(ROOT_ID);
  const [jobLimit, setJobLimit] = useState(JOB_LIMIT);
  const [jobFilter, setJobFilter] = useState("");

  const root = useMemo(() => {
    const roots = hierarchy?.roots || [];
    const { subs, jobs } = rollupRoots(roots);
    return {
      function_id: ROOT_ID,
      name: selectedCompany?.name || "Company",
      children: roots,
      jobs: [],
      _subs: subs,
      _jobs: jobs,
      isCompany: true,
    };
  }, [hierarchy, selectedCompany]);

  // Reset focus if the company (and therefore the tree) changes.
  useEffect(() => {
    setFocusId(ROOT_ID);
    setJobLimit(JOB_LIMIT);
    setJobFilter("");
  }, [selectedCompany?.company_id, hierarchy]);

  const focus =
    focusId === ROOT_ID ? root : hierarchy?.byId?.get(Number(focusId)) || root;

  const path = useMemo(() => {
    if (focus.function_id === ROOT_ID) return [root];
    return [root, ...pathToNode(hierarchy.byId, focus.function_id)];
  }, [focus, hierarchy, root]);

  const goFocus = (id) => {
    setFocusId(id);
    setJobLimit(JOB_LIMIT);
    setJobFilter("");
  };

  const children = focus.children || [];
  const jobs = focus.jobs;
  const filteredJobs = useMemo(() => {
    const q = jobFilter.trim().toLowerCase();
    return q ? jobs.filter((j) => jobMatches(j, q)) : jobs;
  }, [jobs, jobFilter]);
  const shownJobs = filteredJobs.slice(0, jobLimit);
  const remainingJobs = filteredJobs.length - shownJobs.length;

  return (
    <div className="hv-panel">
      <div className="hv-chart-head">
        <div className="hv-crumbs">
          {path.map((n, i) => {
            const last = i === path.length - 1;
            return (
              <React.Fragment key={String(n.function_id)}>
                {i > 0 && <span className="hv-sep">›</span>}
                {last ? (
                  <span className="hv-crumb-current">{n.name}</span>
                ) : (
                  <button
                    type="button"
                    className="hv-crumb-btn"
                    onClick={() => goFocus(n.function_id)}
                  >
                    {n.name}
                  </button>
                )}
              </React.Fragment>
            );
          })}
        </div>
        <span className="hv-chart-hint">
          Click a card to drill in.
        </span>
      </div>

      <div className="hv-chart-scroll">
        <div className="hv-chart-inner">
          {/* Focused node */}
          <div
            className={`hv-node-card ${focus.isCompany ? "hv-root-card" : ""}`}
          >
            <span className="hv-ic">
              <i
                className={`fa ${focus.isCompany ? "fa-building" : "fa-folder-open"}`}
                aria-hidden="true"
              />
            </span>
            <span className="hv-nc-text">
              <span className="hv-nm">{focus.name}</span>
              {countLabel(focus) && (
                <span className="hv-meta">{countLabel(focus)}</span>
              )}
            </span>
          </div>

          {/* Child function cards */}
          {children.length > 0 && (
            <>
              <div className="hv-connector" />
              <div className="hv-children-row">
                {children.map((c) => {
                  const drillable =
                    c.children.length > 0 || c.jobs.length > 0;
                  const icon = c.children.length ? "fa-folder" : "fa-briefcase";
                  return (
                    <div
                      key={c.function_id}
                      className={`hv-child ${children.length === 1 ? "only" : ""}`}
                    >
                      {drillable ? (
                        <button
                          type="button"
                          className="hv-node-card hv-fn-card"
                          onClick={() => goFocus(c.function_id)}
                        >
                          <span className="hv-ic">
                            <i className={`fa ${icon}`} aria-hidden="true" />
                          </span>
                          <span className="hv-nc-text">
                            <span className="hv-nm">{c.name}</span>
                            <span className="hv-meta">{countLabel(c)}</span>
                          </span>
                          <span className="hv-chev">›</span>
                        </button>
                      ) : (
                        <div
                          className="hv-node-card hv-leaf-card"
                          title="No sub-functions or jobs"
                        >
                          <span className="hv-ic">
                            <i className={`fa ${icon}`} aria-hidden="true" />
                          </span>
                          <span className="hv-nc-text">
                            <span className="hv-nm">{c.name}</span>
                            <span className="hv-meta">Empty</span>
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* Jobs of the focused node */}
          {jobs.length > 0 && (
            <div className="hv-jobs-block">
              <div className="hv-jb-head">
                <i className="fa fa-briefcase" aria-hidden="true" /> Jobs in{" "}
                {focus.name} (
                {filteredJobs.length === jobs.length ?
                  jobs.length.toLocaleString()
                : `${filteredJobs.length.toLocaleString()} of ${jobs.length.toLocaleString()}`}
                )
                {jobs.length > JOB_FILTER_MIN && (
                  <label className="hv-search hv-jb-filter">
                    <i className="fa fa-search" aria-hidden="true" />
                    <input
                      type="text"
                      value={jobFilter}
                      placeholder="Filter jobs…"
                      onChange={(e) => {
                        setJobFilter(e.target.value);
                        setJobLimit(JOB_LIMIT);
                      }}
                    />
                    {jobFilter && (
                      <button
                        type="button"
                        className="hv-search-clear"
                        onClick={() => setJobFilter("")}
                        aria-label="Clear job filter"
                      >
                        <i className="fa fa-times" aria-hidden="true" />
                      </button>
                    )}
                  </label>
                )}
              </div>
              {filteredJobs.length === 0 && (
                <div className="hv-leaf-note">No jobs match “{jobFilter}”.</div>
              )}
              <div className="hv-jobs-grid">
                {shownJobs.map((j, i) => (
                  <div key={jobKey(j, i)} className="hv-job-chip">
                    <span className="hv-jic">▪</span>
                    <span className="hv-job-chip-name">{jobName(j)}</span>
                    {jobStaff(j) > 0 && (
                      <span className="hv-job-staff" title="Staff count">
                        <i className="fa fa-users" aria-hidden="true" />{" "}
                        {jobStaff(j)}
                      </span>
                    )}
                  </div>
                ))}
              </div>
              {(remainingJobs > 0 || jobLimit > JOB_LIMIT) && (
                <div className="hv-jobs-more">
                  {remainingJobs > 0 && (
                    <button
                      type="button"
                      className="hv-show-more"
                      onClick={() => setJobLimit((n) => n + JOB_PAGE)}
                    >
                      Show {Math.min(JOB_PAGE, remainingJobs)} more jobs
                      <span className="hv-more-count">
                        {remainingJobs.toLocaleString()} not shown
                      </span>
                    </button>
                  )}
                  {jobLimit > JOB_LIMIT && (
                    <button
                      type="button"
                      className="hv-show-more"
                      onClick={() => setJobLimit(JOB_LIMIT)}
                    >
                      Show fewer
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {children.length === 0 && jobs.length === 0 && (
            <div className="hv-leaf-note">No sub-functions or jobs here.</div>
          )}
        </div>
      </div>

      <div className="hv-legend">
        <span>
          <span className="hv-dot a" /> Department / function
        </span>
        <span>
          <span className="hv-dot g" /> Jobs
        </span>
        <span>“4 sub · 23 jobs” = size of the whole branch.</span>
      </div>
    </div>
  );
};

export default HierarchyChartView;
